import { DetailedStore } from '@spa/components/storefinder/interfaces';
import type { LinkParam } from '@spa/lib/react/hooks/useLinkAttributes';

/** URL query parameter carrying the header Store Finder search term (ECOM-6191). */
export const STORE_QUERY_PARAM = 'storeQuery';

/**
 * Link params carrying the Store Finder search term onto a store-details or Store Finder link. Empty
 * for a missing or blank term, which the Store Finder would not search for anyway.
 */
export const getStoreQueryLinkParams = (
  searchTerm: string | null | undefined
): ReadonlyArray<LinkParam> => {
  return searchTerm?.trim() ? [{ key: STORE_QUERY_PARAM, value: searchTerm }] : [];
};

/**
 * The `?storeQuery=...` search string carrying the Store Finder search term on a programmatic
 * navigation, with the same blank-term rule as {@link getStoreQueryLinkParams}.
 */
export const getStoreQuerySearch = (searchTerm: string | null | undefined): string => {
  return searchTerm?.trim() ? `?${STORE_QUERY_PARAM}=${encodeURIComponent(searchTerm)}` : '';
};

type StoreSlugPart = {
  removeCombiningDiacriticalMarks: () => StoreSlugPart;
  replaceGermanEszett: () => StoreSlugPart;
  replaceGermanUmlauts: () => StoreSlugPart;
  toString: () => string;
};

const createStoreSlugPart = (value: string): StoreSlugPart => {
  return {
    removeCombiningDiacriticalMarks: (): StoreSlugPart => {
      return createStoreSlugPart(value.replace(/[\u0300-\u036f]/g, ''));
    },
    replaceGermanEszett: (): StoreSlugPart => {
      return createStoreSlugPart(value.replaceAll('ß', 'ss').replaceAll('ẞ', 'SS'));
    },
    replaceGermanUmlauts: (): StoreSlugPart => {
      return createStoreSlugPart(
        value
          .replaceAll('ä', 'ae')
          .replaceAll('ö', 'oe')
          .replaceAll('ü', 'ue')
          .replaceAll('Ä', 'Ae')
          .replaceAll('Ö', 'Oe')
          .replaceAll('Ü', 'Ue')
      );
    },
    toString: (): string => {
      return value
        .toLowerCase()
        .normalize('NFD')
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
    }
  };
};

const normalizeStoreSlugPart = (value: string): string => {
  return createStoreSlugPart(value)
    .replaceGermanUmlauts()
    .replaceGermanEszett()
    .removeCombiningDiacriticalMarks()
    .toString();
};

/**
 * Title-safe sanitizer for the client-side Store Finder page title (AC3).
 *
 * Unlike {@link normalizeStoreSlugPart} (which lowercases and hyphenates for URL slugs), this keeps
 * the search term human-readable: it preserves letters (incl. umlauts/diacritics), digits, spaces and
 * a small set of readable punctuation, and strips any other character (`<`, `>`, `&`, quotes, slashes,
 * control chars, …) so nothing unsafe reaches the `<title>` tag. Whitespace is collapsed and trimmed.
 */
export const sanitizeStoreSearchTitle = (value: string): string => {
  return value
    .normalize('NFC')
    .replace(/[^\p{L}\p{N}\s.,'-]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const extractStreetNameFromStreet = (address: string): string => {
  return address.replace(/\s+\d+(?:[a-zA-Z]|[/-]\d+)?\s*$/u, '').trim();
};

export const getStoreSlug = (store: DetailedStore): string => {
  const {
    address: { street, town },
    code
  } = store;

  const normalizedTown = normalizeStoreSlugPart(town);
  const streetName = extractStreetNameFromStreet(street);
  const normalizedStreet = normalizeStoreSlugPart(streetName);

  return [normalizedTown, normalizedStreet, code].filter((part) => part.length > 0).join('-');
};

export const extractStoreCodeFromSlug = (slug: string): string => {
  const parts = slug.replaceAll('/', '').split('-');

  return parts[parts.length - 1];
};
